import React, { useId } from 'react';

export interface LevelSensorSvgProps {
  value?: number | undefined; // Physical level (0 to maxLevel)
  type?: string | undefined; // '0-10V' | '4-20mA'
  rawValue?: number | undefined; // PLC raw integer (0 - 27648)
  minLevel?: number | undefined;
  maxLevel?: number | undefined;
  levelPercent?: number | undefined;
  color?: string | undefined; // Housing color (default: industrial blue '#0284c7')
  isMountedOnTank?: boolean | undefined; // true if mounted on tank dome
  isFaulted?: boolean | undefined; // Wire break / fault state
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

export const LevelSensorSvg: React.FC<LevelSensorSvgProps> = ({
  value = 0,
  type = '0-10V',
  rawValue = 0,
  minLevel = 0,
  maxLevel = 1000,
  levelPercent,
  color = '#0284c7',
  isMountedOnTank = false,
  isFaulted = false,
  className,
  style,
}) => {
  const uid = useId().replace(/:/g, '_');

  // Compute percentage (0 - 100%)
  const span = Math.max(1, maxLevel - minLevel);
  const calculatedPercent = ((value - minLevel) / span) * 100;
  const clampedPercent = Math.max(0, Math.min(100, levelPercent ?? calculatedPercent));

  // Analog electrical readout
  let analogDisplay = '0.00 V';
  if (type === '4-20mA') {
    const mA = 4 + (clampedPercent / 100) * 16;
    analogDisplay = `${mA.toFixed(1)} mA`;
  } else {
    const v = (clampedPercent / 100) * 10;
    analogDisplay = `${v.toFixed(1)} V`;
  }

  // Float Y position along the probe rod (rod spans from y=48 to y=148, delta = 100px)
  const floatY = 148 - (clampedPercent / 100) * 96;

  // Housing color styling
  const housingBase = color || '#0284c7';

  return (
    <g className={className} style={style}>
      <defs>
        {/* Transmitter Cylindrical Body 3D Gradient */}
        <linearGradient id={`transBodyGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0f172a" />
          <stop offset="15%" stopColor={housingBase} />
          <stop offset="38%" stopColor="#ffffff" stopOpacity="0.4" />
          <stop offset="60%" stopColor={housingBase} />
          <stop offset="90%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Bezel Ring Metallic Gradient */}
        <linearGradient id={`bezelGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#cbd5e1" />
          <stop offset="35%" stopColor="#f8fafc" />
          <stop offset="65%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Stainless Steel Probe Rod & Flange Gradient */}
        <linearGradient id={`probeSteelGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="28%" stopColor="#cbd5e1" />
          <stop offset="55%" stopColor="#ffffff" />
          <stop offset="78%" stopColor="#94a3b8" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Magnetic Float Ring Gradient */}
        <linearGradient id={`floatRingGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="30%" stopColor="#94a3b8" />
          <stop offset="70%" stopColor="#475569" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Glass Screen Reflection Gradient */}
        <linearGradient id={`lcdGlassGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.2)" />
        </linearGradient>

        {/* LED Heartbeat Glow Filter */}
        <filter id={`ledHeartbeatGlow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.5" floodColor={isFaulted ? '#ef4444' : '#22c55e'} floodOpacity="0.85" />
        </filter>
      </defs>

      <style>{`
        @keyframes sensor-heartbeat-${uid} {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        .sensor-led-pulse-${uid} {
          animation: sensor-heartbeat-${uid} 1.2s ease-in-out infinite;
        }
      `}</style>

      {/* --- STANDALONE MEASURING PROBE ROD & FLOAT (Freestanding Mode) --- */}
      {!isMountedOnTank && (
        <g data-testid="sensor-standalone-probe">
          {/* Main 316L Stainless Steel Central Measuring Probe Tube */}
          <rect
            x="20.5"
            y="43"
            width="3"
            height="111"
            fill={`url(#probeSteelGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.6"
          />

          {/* Dynamic Steel Specular Line */}
          <line x1="21.8" y1="44" x2="21.8" y2="153" stroke="rgba(255, 255, 255, 0.75)" strokeWidth="0.6" />

          {/* Depth Scale Graduation Tick Marks */}
          <g opacity="0.85" stroke="#94a3b8" strokeWidth="0.7">
            {/* 100% Mark */}
            <line x1="14" y1="52" x2="19" y2="52" />
            {/* 75% Mark */}
            <line x1="15.5" y1="76" x2="19" y2="76" />
            {/* 50% Mark */}
            <line x1="14" y1="100" x2="19" y2="100" />
            {/* 25% Mark */}
            <line x1="15.5" y1="124" x2="19" y2="124" />
            {/* 0% Mark */}
            <line x1="14" y1="148" x2="19" y2="148" />
          </g>

          {/* Scale Labels */}
          <text x="12" y="53.5" fill="#94a3b8" fontSize="3.5" textAnchor="end" fontFamily="monospace">100</text>
          <text x="12" y="101.5" fill="#94a3b8" fontSize="3.5" textAnchor="end" fontFamily="monospace">50</text>
          <text x="12" y="149.5" fill="#94a3b8" fontSize="3.5" textAnchor="end" fontFamily="monospace">0</text>

          {/* Magnetic Float Ring Tracking Liquid Level */}
          <g
            data-testid="sensor-magnetic-float"
            style={{
              transform: `translateY(${floatY - 148}px)`,
              transformOrigin: '22px 148px',
              transition: 'transform 0.25s ease-out',
            }}
          >
            {/* Float Body */}
            <rect
              x="15"
              y="144"
              width="14"
              height="7"
              rx="2"
              fill={`url(#floatRingGrad-${uid})`}
              stroke="#0f172a"
              strokeWidth="0.7"
            />
            {/* Magnetic Toroid Groove */}
            <line x1="15" y1="147.5" x2="29" y2="147.5" stroke="#1e293b" strokeWidth="0.8" />
            <line x1="15" y1="148.2" x2="29" y2="148.2" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="0.5" />
          </g>

          {/* Bottom Probe Stopper / Bumper Anchor */}
          <rect
            x="18"
            y="153"
            width="8"
            height="4"
            rx="1"
            fill="#334155"
            stroke="#0f172a"
            strokeWidth="0.6"
          />
          <ellipse cx="22" cy="157" rx="3" ry="1.2" fill="#1e293b" stroke="#0f172a" strokeWidth="0.5" />
        </g>
      )}

      {/* --- TRANSMITTER HEAD & ELECTRONICS (Upper Unit: y = 0 to 46) --- */}
      <g data-testid="sensor-transmitter-head" data-raw-value={rawValue}>
        <title>{`Level: ${Math.round(clampedPercent)}% (${analogDisplay}) | RAW: ${rawValue}`}</title>
        {/* M20 Cable Gland on Right Side */}
        <rect
          x="36"
          y="15"
          width="6"
          height="8"
          rx="1"
          fill="#475569"
          stroke="#0f172a"
          strokeWidth="0.6"
        />
        {/* Gland Hex Facet Line */}
        <line x1="38.5" y1="15.5" x2="38.5" y2="22.5" stroke="#cbd5e1" strokeWidth="0.5" />
        {/* Signal Cable exiting downward */}
        <path
          d="M 42 19 Q 44 19 44 24 L 44 32"
          fill="none"
          stroke="#090d16"
          strokeWidth="2.2"
          strokeLinecap="round"
        />

        {/* Dual-Compartment Cylindrical Housing Body */}
        <rect
          x="5"
          y="2"
          width="34"
          height="34"
          rx="6"
          fill={`url(#transBodyGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Housing Top/Bottom Cooling Ribs */}
        <line x1="8" y1="4" x2="36" y2="4" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="0.6" />
        <line x1="8" y1="34" x2="36" y2="34" stroke="rgba(0, 0, 0, 0.4)" strokeWidth="0.6" />

        {/* Circular Face Beveled Display Bezel Ring */}
        <circle
          cx="22"
          cy="19"
          r="15"
          fill={`url(#bezelGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1"
        />

        {/* Inner Bezel Groove */}
        <circle cx="22" cy="19" r="13.2" fill="#090d16" stroke="#334155" strokeWidth="0.6" />

        {/* 4 Stainless Steel Bezel Screws */}
        <circle cx="10" cy="19" r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />
        <circle cx="34" cy="19" r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />
        <circle cx="22" cy="7" r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />
        <circle cx="22" cy="31" r="0.8" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />

        {/* High-Contrast LCD Screen Glass Window */}
        <rect
          x="10.5"
          y="8.5"
          width="23"
          height="21"
          rx="2.5"
          fill="#031d2e"
          stroke="#0284c7"
          strokeWidth="0.7"
        />

        {/* Glass Specular Reflection Highlight */}
        <path
          d="M 11 15 Q 22 9 33 15"
          fill="none"
          stroke="rgba(255, 255, 255, 0.35)"
          strokeWidth="0.8"
          strokeLinecap="round"
        />

        {/* --- LCD DISPLAY READOUTS --- */}
        {/* Primary Level Value (e.g. 65%) */}
        <text
          x="22"
          y="16.5"
          fill="#38bdf8"
          fontSize="5.8"
          fontWeight="900"
          textAnchor="middle"
          letterSpacing="0.2"
          fontFamily="monospace"
        >
          {Math.round(clampedPercent)}%
        </text>

        {/* Analog Electrical Readout (e.g. 6.5 V or 14.4 mA) */}
        <text
          x="22"
          y="21.8"
          fill="#f8fafc"
          fontSize="3.2"
          fontWeight="700"
          textAnchor="middle"
          fontFamily="monospace"
        >
          {analogDisplay}
        </text>

        {/* Mode Tag (0-10V / 4-20mA) */}
        <text
          x="22"
          y="25.5"
          fill="#7dd3fc"
          fontSize="2.4"
          fontWeight="600"
          textAnchor="middle"
          fontFamily="monospace"
        >
          {type}
        </text>

        {/* Mini Analog Horizontal Bar Graph (0-100%) */}
        <rect
          x="12"
          y="26.8"
          width="20"
          height="1.6"
          rx="0.5"
          fill="#0c4a6e"
        />
        <rect
          x="12"
          y="26.8"
          width={Math.max(1, (clampedPercent / 100) * 20)}
          height="1.6"
          rx="0.5"
          fill={isFaulted ? '#ef4444' : '#38bdf8'}
        />

        {/* Heartbeat Status LED Indicator on Bezel */}
        <circle
          cx="30.5"
          cy="11.5"
          r="1.4"
          fill={isFaulted ? '#ef4444' : '#22c55e'}
          stroke="#0f172a"
          strokeWidth="0.4"
          filter={`url(#ledHeartbeatGlow-${uid})`}
          className={`sensor-led-pulse-${uid}`}
          data-testid="sensor-status-led"
        />

        {/* Stainless Steel Neck Coupling & Process Flange */}
        <g data-testid="sensor-process-flange">
          {/* Neck Tube */}
          <rect
            x="18.5"
            y="36"
            width="7"
            height="4"
            fill={`url(#probeSteelGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.6"
          />
          {/* Heavy-Duty Flange Collar */}
          <rect
            x="13"
            y="40"
            width="18"
            height="4.5"
            rx="1.2"
            fill={`url(#probeSteelGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="0.8"
          />
          {/* Flange Bolts */}
          <circle cx="15.5" cy="42.2" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />
          <circle cx="28.5" cy="42.2" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.3" />
        </g>
      </g>
    </g>
  );
};
